// ** react imports
import React from 'react';

// ** react router imports
import { useNavigate } from 'react-router-dom';

// ** validation imports
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

// ** custom hooks imports
import { useCreateAnnouncement } from '../../../hooks/apis/useAnnouncement';

// ** components imports
import LoadingButton from '../../../components/common/Buttons/LoadingButton';
import NormalButton from '../../../components/common/Buttons/NormalButton';
import NormalInput from '../../../components/common/Inputs/NormalInput';
import SelectInput from '../../../components/common/Inputs/SelectInput';
import SwitchInput from '../../../components/common/Inputs/SwitchInput';
import Textarea from '../../../components/common/Inputs/Textarea';

// ** enums imports
import { BUTTON_THEMES } from '../../../enums/common';

const RenderFormSection = props => {
  const { children } = props;

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg mb-6 overflow-hidden">
      <div className="p-4">{children}</div>
    </div>
  );
};

const AddAnnouncement = () => {
  // hooks
  const navigate = useNavigate();

  // validation schema
  const validationSchema = yup.object().shape({
    title: yup
      .string()
      .required('Title is required')
      .max(200, 'Title must be at most 200 characters')
      .trim(),
    description: yup.string().required('Description is required').trim(),
    publishDate: yup
      .string()
      .required('Publish date is required')
      .matches(
        /^\d{4}-\d{2}-\d{2}$/,
        'Publish date must be in YYYY-MM-DD format'
      ),
    expiryDate: yup
      .string()
      .nullable()
      .matches(
        /^\d{4}-\d{2}-\d{2}$/,
        'Expiry date must be in YYYY-MM-DD format'
      ),
    urgency: yup
      .string()
      .nullable()
      .oneOf(
        ['low', 'medium', 'high', 'critical'],
        'Urgency must be low, medium, high, or critical'
      ),
    category: yup
      .string()
      .nullable()
      .oneOf(
        ['info', 'warning', 'error', 'success', 'maintenance'],
        'Category must be info, warning, error, success, or maintenance'
      ),
    isActive: yup.boolean().nullable(),
    targetAudiences: yup
      .array()
      .of(yup.string().oneOf(['all', 'customer', 'manager', 'admin']))
      .nullable()
      .default([]),
    priority: yup
      .number()
      .nullable()
      .integer('Priority must be an integer')
      .min(0, 'Priority must be a non-negative integer'),
    isGlobal: yup.boolean().nullable(),
    isPinned: yup.boolean().nullable(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
    defaultValues: {
      title: '',
      description: '',
      publishDate: '',
      expiryDate: '',
      urgency: null,
      category: null,
      isActive: true,
      targetAudiences: [],
      priority: 0,
      isGlobal: false,
      isPinned: false,
    },
  });

  const { isCreateAnnouncementLoading, createAnnouncement } =
    useCreateAnnouncement();

  const onSubmit = async data => {
    try {
      const response = await createAnnouncement(data);

      if (response.data?.success) {
        reset();
        setTimeout(() => {
          navigate('/announcements/list');
        }, 150);
      } else if (response.error) {
        console.error('Error creating announcement:', response.error);
      }
    } catch (error) {
      console.error('Error creating announcement:', error);
    }
  };

  const handleCancel = () => {
    navigate('/announcements/list');
  };

  const renderErrorMessage = error => {
    if (!error) return null;
    return <p className="mt-2 text-xs text-red-500">{error}</p>;
  };

  // Watch values for conditional rendering
  const isGlobal = watch('isGlobal');
  const hasExpiry = !!watch('expiryDate');

  return (
    <div className="flex">
      <main className="grow">
        <div className="px-4 sm:px-6 lg:px-8 py-6 w-full max-w-[96rem] mx-auto">
          {/* Page header */}
          <div className="sm:flex sm:justify-between sm:items-center mb-6">
            {/* Left: Title */}
            <div className="mb-4 sm:mb-0">
              <h1 className="text-2xl md:text-3xl text-gray-800 dark:text-gray-100 font-bold">
                Create Announcement
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Create a new announcement with all required details
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white dark:bg-gray-800 shadow-xs rounded-xl mb-8">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="p-6 space-y-6">
                {/* Primary Information Section */}
                <RenderFormSection>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <NormalInput
                        label="Title"
                        type="text"
                        name="title"
                        register={register}
                        errors={errors.title?.message}
                        required
                        maxLength={200}
                        placeholder="Enter announcement title"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <Textarea
                        label="Description"
                        name="description"
                        register={register}
                        errors={errors.description?.message}
                        required
                        placeholder="Enter announcement description"
                        rows={4}
                      />
                    </div>

                    <div>
                      <NormalInput
                        label="Publish Date"
                        type="date"
                        name="publishDate"
                        register={register}
                        errors={errors.publishDate?.message}
                        required
                      />
                    </div>

                    <div>
                      <NormalInput
                        label="Expiry Date"
                        type="date"
                        name="expiryDate"
                        register={register}
                        errors={errors.expiryDate?.message}
                        placeholder="Optional"
                      />
                    </div>

                    <div>
                      <SelectInput
                        label="Urgency"
                        name="urgency"
                        options={[
                          { label: 'Low', value: 'low' },
                          { label: 'Medium', value: 'medium' },
                          { label: 'High', value: 'high' },
                          { label: 'Critical', value: 'critical' },
                        ]}
                        register={register}
                        errors={errors.urgency?.message}
                      />
                    </div>

                    <div>
                      <SelectInput
                        label="Category"
                        name="category"
                        options={[
                          { label: 'Info', value: 'info' },
                          { label: 'Warning', value: 'warning' },
                          { label: 'Error', value: 'error' },
                          { label: 'Success', value: 'success' },
                          { label: 'Maintenance', value: 'maintenance' },
                        ]}
                        register={register}
                        errors={errors.category?.message}
                      />
                    </div>

                    <div>
                      <NormalInput
                        label="Priority"
                        type="number"
                        name="priority"
                        register={register}
                        errors={errors.priority?.message}
                        min="0"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </RenderFormSection>

                {/* Targeting & Visibility Section */}
                <RenderFormSection>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Controller
                        name="isActive"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <SwitchInput
                            label="Is Active"
                            checked={value}
                            onChange={e => onChange(e.target.checked)}
                            errors={errors.isActive?.message}
                          />
                        )}
                      />
                    </div>

                    <div className="md:col-span-2">
                      <Controller
                        name="targetAudiences"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Target Audiences
                            </label>
                            <div className="flex flex-wrap gap-3">
                              {[
                                { label: 'All', value: 'all' },
                                {
                                  label: 'Customer',
                                  value: 'customer',
                                },
                                {
                                  label: 'Manager',
                                  value: 'manager',
                                },
                                {
                                  label: 'Admin',
                                  value: 'admin',
                                },
                              ].map(option => (
                                <label
                                  key={option.value}
                                  className="flex items-center"
                                >
                                  <input
                                    type="checkbox"
                                    className="form-checkbox h-4 w-4 text-blue-60"
                                    checked={
                                      value?.includes(option.value) || false
                                    }
                                    onChange={e => {
                                      if (e.target.checked) {
                                        onChange([
                                          ...(value || []),
                                          option.value,
                                        ]);
                                      } else {
                                        onChange(
                                          value?.filter(
                                            v => v !== option.value
                                          ) || []
                                        );
                                      }
                                    }}
                                  />
                                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                                    {option.label}
                                  </span>
                                </label>
                              ))}
                            </div>
                            {renderErrorMessage(
                              errors.targetAudiences?.message
                            )}
                          </div>
                        )}
                      />
                    </div>
                  </div>
                </RenderFormSection>

                {/* Form Footer */}
                <footer>
                  <div className="flex flex-col px-6 py-5 border-t border-gray-200 dark:border-gray-700/60">
                    <div className="flex self-end gap-3">
                      <NormalButton
                        theme={BUTTON_THEMES.DANGER}
                        onClick={() => handleCancel()}
                      >
                        Cancel
                      </NormalButton>
                      <LoadingButton
                        type="submit"
                        theme={BUTTON_THEMES.PRIMARY}
                        isLoading={isCreateAnnouncementLoading}
                      >
                        Create Announcement
                      </LoadingButton>
                    </div>
                  </div>
                </footer>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AddAnnouncement;
